import React from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  CalendarDays,
  Send,
  MailCheck,
  Compass,
  MessageCircle,
  Plane,
  BookOpen,
  HeartHandshake,
  ArrowRight,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const COPY = {
  overline: { en: "Booking · Xaluca", fr: "Réservation · Xaluca", es: "Reserva · Xaluca" },
  title: { en: "How to book", fr: "Comment réserver", es: "Cómo reservar" },
  title_accent: { en: "with us", fr: "avec nous", es: "con nosotros" },
  title_hotel: { en: "How to book your", fr: "Comment réserver votre", es: "Cómo reservar su" },
  title_hotel_accent: { en: "Xaluca journey", fr: "voyage Xaluca", es: "viaje Xaluca" },
  sub: {
    en: "Five quiet steps between intention and arrival — accompanied at every moment by a host, never a bot. The same warm process applies to every Grup Xaluca address: our kasbahs, riads, hotels, mountain refuge and the Saharan camps.",
    fr: "Cinq étapes feutrées entre l'intention et l'arrivée — accompagnées à chaque instant par un hôte, jamais par un bot. Le même rituel s'applique à chacune de nos adresses : kasbahs, riads, hôtels, refuge de montagne et camps sahariens.",
    es: "Cinco pasos serenos entre la intención y la llegada — acompañados en cada momento por un anfitrión, nunca por un bot. El mismo ritual se aplica a cada dirección de Grup Xaluca: kasbahs, riads, hoteles, refugio de montaña y campamentos saharianos.",
  },
  touchpoints_overline: { en: "Beyond the form", fr: "Au-delà du formulaire", es: "Más allá del formulario" },
  cta_primary: { en: "Begin a booking enquiry", fr: "Démarrer une demande", es: "Iniciar una solicitud" },
  cta_secondary: { en: "Speak to a Xaluca host", fr: "Parler à un hôte Xaluca", es: "Hablar con un anfitrión" },
  cta_primary_here: { en: "Use the planner above", fr: "Utilisez le planificateur ci-dessus", es: "Use el planificador de arriba" },
  footnote: {
    en: "No deposit required to enquire. We hold the conversation, not your card.",
    fr: "Aucun acompte requis pour s'informer. Nous gardons la conversation, pas votre carte.",
    es: "Sin depósito para consultar. Mantenemos la conversación, no tu tarjeta.",
  },
};

const STEPS = [
  {
    key: "destination",
    Icon: MapPin,
    title: {
      en: "Choose your destination",
      fr: "Choisissez votre destination",
      es: "Elige tu destino",
    },
    desc: {
      en: "Browse the nine Xaluca addresses — a kasbah in Arfoud, a riad in Erg Chebbi, a refuge in the M'Goun, a luxury bivouac in the dunes. Pick one or stitch several together.",
      fr: "Parcourez les neuf adresses Xaluca — une kasbah à Arfoud, un riad à l'Erg Chebbi, un refuge dans le M'Goun, un bivouac de luxe dans les dunes. Choisissez-en une ou tissez-en plusieurs.",
      es: "Recorre las nueve direcciones Xaluca — una kasbah en Arfoud, un riad en Erg Chebbi, un refugio en el M'Goun, un vivac de lujo en las dunas. Elige una o combina varias.",
    },
  },
  {
    key: "dates",
    Icon: CalendarDays,
    title: {
      en: "Pick your dates",
      fr: "Choisissez vos dates",
      es: "Elige tus fechas",
    },
    desc: {
      en: "Tell us the dates you have in mind, or the loose week you're imagining. The southern light shifts every season — we can advise on the best window for your dream.",
      fr: "Indiquez les dates précises ou la semaine approximative qui vous habite. La lumière du sud change à chaque saison — nous vous conseillons la fenêtre la plus belle pour votre projet.",
      es: "Indícanos las fechas exactas o la semana aproximada que tienes en mente. La luz del sur cambia con cada estación — te aconsejaremos la mejor ventana para tu sueño.",
    },
  },
  {
    key: "request",
    Icon: Send,
    title: {
      en: "Send your enquiry",
      fr: "Envoyez votre demande",
      es: "Envía tu solicitud",
    },
    desc: {
      en: "Submit the booking form or write directly to a host. Mention rooms, suites, bivouac tents, experiences, transfers — anything you'd like considered.",
      fr: "Envoyez le formulaire ou écrivez à un hôte. Mentionnez chambres, suites, tentes de bivouac, expériences, transferts — tout ce que vous souhaitez prendre en compte.",
      es: "Envía el formulario o escribe directamente a un anfitrión. Menciona habitaciones, suites, tiendas de vivac, experiencias, traslados — todo lo que quieras considerar.",
    },
  },
  {
    key: "confirmation",
    Icon: MailCheck,
    title: {
      en: "Receive a tailored proposal",
      fr: "Recevez une proposition sur mesure",
      es: "Recibe una propuesta a medida",
    },
    desc: {
      en: "Within 24 hours, a host returns with availability, a draft programme and recommendations — restaurants for your taste, excursions for your pace, a route that breathes.",
      fr: "Dans les 24 heures, un hôte vous répond — disponibilités, programme proposé et recommandations — restaurants à votre goût, excursions à votre rythme, un itinéraire qui respire.",
      es: "En menos de 24 horas, un anfitrión responde — disponibilidad, programa propuesto y recomendaciones — restaurantes a tu gusto, excursiones a tu ritmo, una ruta que respira.",
    },
  },
  {
    key: "finalize",
    Icon: Compass,
    title: {
      en: "Confirm & prepare",
      fr: "Confirmez & préparez",
      es: "Confirma y prepara",
    },
    desc: {
      en: "Once you say yes, we send the practical map — flights, transfers, packing notes, the weather of your week. Then we wait, the door open.",
      fr: "Une fois votre oui reçu, nous vous envoyons la carte pratique — vols, transferts, conseils de bagage, la météo de votre semaine. Puis nous attendons, la porte ouverte.",
      es: "Una vez que digas sí, te enviamos el mapa práctico — vuelos, traslados, consejos de equipaje, el clima de tu semana. Luego esperamos, con la puerta abierta.",
    },
  },
];

const TOUCHPOINTS = [
  {
    Icon: MessageCircle,
    title: { en: "Contact a host directly", fr: "Contactez un hôte directement", es: "Contacte a un anfitrión" },
    desc: { en: "Phone, email or WhatsApp — answered by the same family that runs the houses.", fr: "Téléphone, e-mail ou WhatsApp — réponse de la famille qui tient les maisons.", es: "Teléfono, correo o WhatsApp — responde la familia que lleva las casas." },
  },
  {
    Icon: Plane,
    title: { en: "Coordinated transfers", fr: "Transferts coordonnés", es: "Traslados coordinados" },
    desc: { en: "Airport pickups, inter-hotel drives, 4×4 dune transfers — arranged on request.", fr: "Aéroport, trajets inter-hôtels, transferts 4×4 dans les dunes — sur demande.", es: "Aeropuerto, trayectos entre hoteles, traslados 4×4 en las dunas — bajo petición." },
  },
  {
    Icon: BookOpen,
    title: { en: "Bespoke itinerary", fr: "Itinéraire sur mesure", es: "Itinerario a medida" },
    desc: { en: "Multi-stay route across kasbahs, mountains and dunes — written for your rhythm.", fr: "Route multi-étapes entre kasbahs, montagnes et dunes — écrite à votre rythme.", es: "Ruta multi-etapa entre kasbahs, montañas y dunas — escrita a tu ritmo." },
  },
  {
    Icon: HeartHandshake,
    title: { en: "A human, every step", fr: "Un humain, à chaque étape", es: "Un humano en cada paso" },
    desc: { en: "From first email to last sunset — one host stays with your booking, from end to end.", fr: "Du premier message au dernier coucher de soleil — un hôte reste avec votre réservation, de bout en bout.", es: "Del primer correo al último atardecer — un anfitrión acompaña tu reserva de principio a fin." },
  },
];

const HowToBookSection = ({ hotel } = {}) => {
  const { lang } = useLanguage();
  const bookingHref = hotel?.slug
    ? `/booking?hotel=${encodeURIComponent(hotel.slug)}`
    : "/booking";
  const contactHref = hotel?.slug
    ? `/contact?topic=stay-${encodeURIComponent(hotel.slug)}`
    : "/contact";

  return (
    <section
      id="how-to-book"
      className="relative py-24 md:py-32 bg-[#FDFBF7] border-t border-[#2C2621]/10 overflow-hidden"
      data-testid="section-how-to-book"
    >
      {/* Soft warm accent wash, top-right */}
      <div
        className="absolute inset-0 pointer-events-none opacity-60"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 90% -10%, rgba(212,163,115,0.18) 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-8 mb-16 md:mb-20">
          <div className="md:col-span-7">
            <p className="overline">{pick(COPY.overline, lang)}</p>
            <h2 className="font-serif-x mt-5 text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#2C2621]">
              {hotel?.slug ? (
                <>
                  {pick(COPY.title_hotel, lang)}{" "}
                  <em className="not-italic text-[#C16542]">{pick(COPY.title_hotel_accent, lang)}</em>
                </>
              ) : (
                <>
                  {pick(COPY.title, lang)}{" "}
                  <em className="not-italic text-[#C16542]">{pick(COPY.title_accent, lang)}</em>
                </>
              )}
            </h2>
          </div>
          <div className="md:col-span-5 flex md:items-end">
            <p className="text-base md:text-lg leading-relaxed text-[#5C5248]">
              {pick(COPY.sub, lang)}
            </p>
          </div>
        </div>

        {/* Timeline of 5 steps */}
        <div className="relative" data-testid="how-to-book-timeline">
          {/* Gold horizontal connecting line (desktop only) */}
          <span
            className="hidden lg:block absolute top-14 left-[8%] right-[8%] h-px bg-gradient-to-r from-transparent via-[#D4A373]/55 to-transparent"
            aria-hidden="true"
          />

          <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-y-12 gap-x-6 lg:gap-x-3">
            {STEPS.map((s, i) => {
              const Icon = s.Icon;
              return (
                <li
                  key={s.key}
                  className="relative group htb-step-reveal"
                  style={{ animationDelay: `${i * 90}ms` }}
                  data-testid={`how-to-book-step-${s.key}`}
                >
                  {/* Numbered glyph + icon disk */}
                  <div className="flex flex-col items-start lg:items-center text-left lg:text-center">
                    <div className="relative">
                      <span className="absolute -top-2 -left-6 font-serif-x text-6xl md:text-7xl text-[#2C2621]/[0.06] leading-none tracking-tighter select-none">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="relative inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#FDFBF7] border border-[#D4A373]/55 text-[#A07042] shadow-[0_0_0_6px_rgba(253,251,247,1)] group-hover:border-[#C16542] group-hover:text-[#C16542] group-hover:scale-105 transition-all duration-500">
                        <Icon className="w-6 h-6 md:w-7 md:h-7" strokeWidth={1.4} />
                      </span>
                    </div>
                    <p className="mt-6 text-[10px] tracking-[0.3em] uppercase text-[#A07042]">
                      {pick({ en: "Step", fr: "Étape", es: "Paso" }, lang)} · {String(i + 1).padStart(2, "0")}
                    </p>
                    <h3 className="mt-3 font-serif-x text-xl md:text-2xl tracking-tight leading-snug text-[#2C2621] max-w-[14rem]">
                      {pick(s.title, lang)}
                    </h3>
                    <p className="mt-3 text-sm md:text-[15px] text-[#5C5248] leading-relaxed max-w-[18rem] lg:max-w-[16rem]">
                      {pick(s.desc, lang)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Touchpoints strip */}
        <div className="mt-24 md:mt-28" data-testid="how-to-book-touchpoints">
          <div className="flex items-baseline justify-between mb-8 gap-6 flex-wrap">
            <p className="overline">{pick(COPY.touchpoints_overline, lang)}</p>
            <span className="h-px flex-1 bg-[#2C2621]/10 hidden sm:block" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#2C2621]/10 border border-[#2C2621]/10">
            {TOUCHPOINTS.map((tp, i) => {
              const Icon = tp.Icon;
              return (
                <div
                  key={`htb-tp-${pick(tp.title, "en") || i}`}
                  className="bg-[#FDFBF7] p-7 md:p-8 flex flex-col gap-4 hover:bg-[#F2EBE1]/40 transition-colors duration-500"
                  data-testid={`how-to-book-touchpoint-${i}`}
                >
                  <span className="inline-flex items-center justify-center w-11 h-11 text-[#A07042] border border-[#D4A373]/45">
                    <Icon className="w-5 h-5" strokeWidth={1.4} />
                  </span>
                  <h4 className="font-serif-x text-lg md:text-xl tracking-tight leading-snug text-[#2C2621]">
                    {pick(tp.title, lang)}
                  </h4>
                  <p className="text-sm text-[#5C5248] leading-relaxed">
                    {pick(tp.desc, lang)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA bar */}
        <div className="mt-16 md:mt-20 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 pt-10 border-t border-[#2C2621]/15">
          <p className="text-sm md:text-base text-[#5C5248] leading-relaxed max-w-xl">
            {pick(COPY.footnote, lang)}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            {hotel?.slug ? (
              <Link
                to={bookingHref}
                className="group inline-flex items-center justify-center gap-3 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-7 md:px-8 py-4 text-[11px] tracking-[0.3em] uppercase transition-colors whitespace-nowrap"
                data-testid="how-to-book-cta-primary"
              >
                {pick(COPY.cta_primary, lang)}
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (typeof window === "undefined") return;
                  // Priority order for the "planner" anchor:
                  //   1. `booking-step-hotels` — the actual "Step 01 ·
                  //      Houses" section on the standalone /booking
                  //      page (and the embedded /contact tab).
                  //   2. `booking-embedded-planner` — historical wrapper
                  //      used when the planner sits inside another
                  //      layout.
                  //   3. `home-booking-widget` — the Home page's
                  //      compact widget variant.
                  // Falling through to scrollTop(0) was the bug users
                  // reported: on /booking neither #2 nor #3 exists, so
                  // clicking "Accede al planificador" bounced back to
                  // the page hero instead of landing on Step 01.
                  const el = document.querySelector("[data-testid='booking-step-hotels']") ||
                             document.querySelector("[data-testid='booking-embedded-planner']") ||
                             document.querySelector("[data-testid='home-booking-widget']");
                  if (el) {
                    // Offset a bit above so the sticky header (~116px)
                    // does not overlap the "Step 01" overline.
                    const y = el.getBoundingClientRect().top + window.scrollY - 120;
                    window.scrollTo({ top: Math.max(y, 0), behavior: "smooth" });
                  } else {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }}
                className="group inline-flex items-center justify-center gap-3 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-7 md:px-8 py-4 text-[11px] tracking-[0.3em] uppercase transition-colors whitespace-nowrap"
                data-testid="how-to-book-cta-primary"
              >
                {pick(COPY.cta_primary_here, lang)}
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform rotate-[-90deg]" strokeWidth={1.5} />
              </button>
            )}
            <Link
              to={contactHref}
              className="group inline-flex items-center justify-center gap-3 border border-[#2C2621]/30 hover:border-[#2C2621] text-[#2C2621] px-7 md:px-8 py-4 text-[11px] tracking-[0.3em] uppercase transition-colors whitespace-nowrap"
              data-testid="how-to-book-cta-secondary"
            >
              {pick(COPY.cta_secondary, lang)}
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </div>

      {/* Local CSS: soft reveal */}
      <style>{`
        @keyframes htb-step-in {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .htb-step-reveal {
          opacity: 0;
          animation: htb-step-in 800ms cubic-bezier(0.22,1,0.36,1) forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          .htb-step-reveal { animation: none; opacity: 1; }
        }
      `}</style>
    </section>
  );
};

export default HowToBookSection;
